import classNames from 'classnames';
import { motion } from 'framer-motion';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useActiveSection } from '@/Domain/hooks/Action/useActiveSection.hook';

type Section = {
  id: string | number;
  label: string;
};

type Props = {
  sections: Section[];
  scrollContainer: HTMLElement | null;
};

export const ScrollNav = ({ sections, scrollContainer }: Props) => {
  const [indicatorTop, setIndicatorTop] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const sectionIds = useMemo(() => sections.map((section) => section.id), [sections]);

  const { activeId, handleScrollToSection } = useActiveSection(sectionIds, scrollContainer);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const activeLink = Array.from(container.querySelectorAll<HTMLElement>('[data-id]')).find(
      (link) => link.dataset.id === String(activeId),
    );
    if (activeLink instanceof HTMLElement) {
      const offset = activeLink.offsetTop + activeLink.offsetHeight - 36;
      setIndicatorTop(offset);
    }
  }, [activeId]);

  return (
    <section className="hidden 2xl:block">
      <p className="text-foreground-subtle text-xs font-semibold mb-5">OVERVIEW</p>

      <nav ref={containerRef} className="relative flex flex-col">
        <motion.span
          layout
          layoutId="nav-indicator"
          className="absolute left-0 w-[3px] h-[36px] bg-foreground"
          style={{ top: indicatorTop }}
          transition={{ type: 'tween', stiffness: 200, damping: 30 }}
        />

        {sections.map(({ id, label }) => (
          <a
            key={id}
            data-id={id}
            data-testid={`scroll-nav-link-${id}`}
            href={`#${id}`}
            onClick={(event) => {
              event.preventDefault();
              handleScrollToSection(id);
            }}
            className={classNames(
              'border-l h-[36px] flex items-center relative text-sm text-left pl-4 cursor-pointer transition-colors duration-300 !no-underline',
              {
                'text-foreground font-semibold': activeId === id,
                'text-muted-foreground font-medium': activeId !== id,
              },
            )}
          >
            {label}
          </a>
        ))}
      </nav>
    </section>
  );
};
